import { Amplify } from 'aws-amplify';
import {
  confirmSignIn,
  fetchAuthSession,
  fetchUserAttributes,
  getCurrentUser,
  signIn,
  signOut,
  updatePassword,
  resetPassword,
  confirmResetPassword,
  sendUserAttributeVerificationCode,
  confirmUserAttribute,
} from 'aws-amplify/auth';

import type { RuntimeConfig } from '../app/config/runtime-config';

export interface CognitoProfileAttributes {
  email: string | null;
  emailVerified: boolean;
}

export function configureCognito(config: RuntimeConfig): void {
  if (config.authMode !== 'cognito') return;
  Amplify.configure({
    Auth: {
      Cognito: {
        userPoolId: config.cognitoUserPoolId,
        userPoolClientId: config.cognitoClientId,
        loginWith: {
          email: true,
          username: true,
        },
      },
    },
  });
}

export async function hasCognitoSession(): Promise<boolean> {
  try {
    await getCurrentUser();
    return true;
  } catch {
    return false;
  }
}

export async function getAccessToken(): Promise<string | null> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.accessToken?.toString() ?? null;
  } catch {
    return null;
  }
}

export async function getCognitoProfileAttributes(): Promise<CognitoProfileAttributes> {
  const attributes = await fetchUserAttributes();
  return {
    email: attributes.email?.trim() || null,
    emailVerified: attributes.email_verified === 'true',
  };
}

export interface NewPasswordChallenge {
  status: 'NEW_PASSWORD_REQUIRED';
  missingAttributes: string[];
}

export type PasswordSignInResult = { status: 'SIGNED_IN' } | NewPasswordChallenge;

export async function signInWithPassword(username: string, password: string): Promise<PasswordSignInResult> {
  const result = await signIn({ username, password });
  if (result.isSignedIn) return { status: 'SIGNED_IN' };
  if (result.nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
    return {
      status: 'NEW_PASSWORD_REQUIRED',
      missingAttributes: (result.nextStep.missingAttributes ?? []).map(String),
    };
  }
  throw new Error(`Unsupported Cognito sign-in step: ${result.nextStep.signInStep}`);
}

export async function confirmNewPassword(
  password: string,
  userAttributes: Record<string, string> = {},
): Promise<void> {
  const result = await confirmSignIn({
    challengeResponse: password,
    ...(Object.keys(userAttributes).length ? { options: { userAttributes } } : {}),
  });
  if (!result.isSignedIn) {
    throw new Error(`Unsupported Cognito sign-in step: ${result.nextStep.signInStep}`);
  }
}

export async function requestCognitoPasswordReset(username: string): Promise<void> {
  await resetPassword({ username });
}

export async function confirmCognitoPasswordReset(username: string, confirmationCode: string, newPassword: string): Promise<void> {
  await confirmResetPassword({ username, confirmationCode, newPassword });
}

export async function changeCognitoPassword(currentPassword: string, newPassword: string): Promise<void> {
  await updatePassword({ oldPassword: currentPassword, newPassword });
}

export async function signOutCognito(): Promise<void> {
  await signOut();
}

export async function requestCognitoEmailVerificationCode(): Promise<void> {
  await sendUserAttributeVerificationCode({ userAttributeKey: 'email' });
}

export async function confirmCognitoEmailVerification(confirmationCode: string): Promise<void> {
  await confirmUserAttribute({ userAttributeKey: 'email', confirmationCode });
}
